/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {forwardRef, useEffect, useMemo, useState, useRef} from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Text,
    HStack,
    Stack,
    Skeleton,
    useBreakpointValue
} from '@salesforce/retail-react-app/app/components/shared/ui'
import ProductTile from '../product-tile'
import {
    PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID_1,
    PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID_2,
    PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID_3,
    PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID_4
} from '../../constants'

import {Swiper, SwiperSlide} from 'swiper/react'
import 'swiper/css'
import 'swiper/css/navigation'
import {Navigation} from 'swiper/modules'
import LoadingSpinner from '../loading-spinner'
import useMonetate from '../../hooks/use-monetate'
import {useWishList} from '../../hooks/use-wish-list'
import {useCustomerId, useShopperCustomersMutation} from '@salesforce/commerce-sdk-react'
import logger from '@salesforce/retail-react-app/app/utils/logger-instance'
import {useGlobaleContext} from '../../contexts/globale'
import useTagManager from '../../hooks/use-tag-manager'
import sliderVariant from './partials/sliderVariant'
import {useLocation} from 'react-router-dom'
import LifeStyleImage from './partials/lifestyle-image'
import {scheduleIdleAnalytics} from '../../utils/common-utils'

const DEFERRED_INITIAL_PRODUCT_COUNT = 4
const DEFERRED_RENDER_BATCH_SIZE = 2
const EMPTY_ANALYTICS_PROPS = {}

/**
 * Renders a scrollable, horizontal container of products with native scroll
 * snapping and manual button controls.
 */
const ProductScroller = forwardRef(
    (
        {
            header,
            title,
            titleMinHeight,
            titleFontFamily,
            isCompleteTheLook = false,
            type = '',
            titleColor = 'black',
            titleTag = 'h2',
            dataLayerLoaded = true,
            products,
            openQuickAddModal = false,
            isLoading,
            hideArrows = true,
            quickAddLabel = true,
            showImageSlider = true,
            hideVariations = false,
            itemWidth = {base: '175px', lg: '300px'},
            productTileProps,
            showQuickAdd = true,
            showTitle = true,
            enableFavourite = false,
            onTileClick = () => {},
            preventTileClick = false,
            spaceBetween = {base: 10, lg: 8},
            showName = true,
            showPrice = true,
            sliderGroup = 'default',
            loop = false,
            grabCursor = false,
            freeMode = false,
            mousewheel = false,
            analyticsProps = EMPTY_ANALYTICS_PROPS,
            showMetalLabel = false,
            lifeStyle = {},
            deferProductRendering = false
        },
        ref
    ) => {
        const spaceBetweenValue = useBreakpointValue(spaceBetween)
        const {countryISO, currencyCode} = useGlobaleContext()
        const {data: wishlist} = useWishList()
        const customerId = useCustomerId()
        const monetate = useMonetate()
        const tagManager = useTagManager()
        const [wishlistLoading, setWishlistLoading] = useState([])
        const [wishlistAnimation, setWishlistAnimation] = useState(false)
        const [showAddtoCartSpinner, setShowAddtoCartSpinner] = useState(false)
        const [wishlistSpinner, setWishlistSpinner] = useState(false)
        const [tileHeight, setTileHeight] = useState('450px')
        const initialProductCount = deferProductRendering
            ? Math.min(products?.length || 0, DEFERRED_INITIAL_PRODUCT_COUNT)
            : products?.length || 0
        const [renderedProductCount, setRenderedProductCount] = useState(initialProductCount)
        const renderedProductCountRef = useRef(initialProductCount)

        useEffect(() => {
            const productCount = products?.length || 0
            const firstBatchCount = deferProductRendering
                ? Math.min(productCount, DEFERRED_INITIAL_PRODUCT_COUNT)
                : productCount

            renderedProductCountRef.current = firstBatchCount
            setRenderedProductCount(firstBatchCount)

            if (!deferProductRendering || firstBatchCount >= productCount) return undefined

            let cancelled = false
            let idleId
            let timeoutId

            const scheduleNextBatch = () => {
                if (cancelled) return

                const revealNextBatch = () => {
                    if (cancelled) return

                    const nextCount = Math.min(
                        productCount,
                        renderedProductCountRef.current + DEFERRED_RENDER_BATCH_SIZE
                    )
                    renderedProductCountRef.current = nextCount
                    setRenderedProductCount(nextCount)

                    if (nextCount < productCount) scheduleNextBatch()
                }

                if (typeof window.requestIdleCallback === 'function') {
                    idleId = window.requestIdleCallback(revealNextBatch, {timeout: 1500})
                } else {
                    timeoutId = window.setTimeout(revealNextBatch, 100)
                }
            }

            scheduleNextBatch()

            return () => {
                cancelled = true
                if (idleId !== undefined && typeof window.cancelIdleCallback === 'function') {
                    window.cancelIdleCallback(idleId)
                }
                if (timeoutId !== undefined) window.clearTimeout(timeoutId)
            }
        }, [deferProductRendering, products])

        const renderedProducts = deferProductRendering
            ? products?.slice(0, renderedProductCount)
            : products
        const productAnalyticsProps = useMemo(
            () =>
                products?.map((product, index) => ({
                    ...analyticsProps,
                    tile_name: product.name || product.productName,
                    tile_position: index + 1
                })) || [],
            [analyticsProps, products]
        )

        // Polling function to make sure customerId & listId are available
        const customerIdRef = useRef(null)
        const listIdRef = useRef(null)

        useEffect(() => {
            if (customerIdRef) {
                customerIdRef.current = customerId
            }
        }, [customerId])
        useEffect(() => {
            if (listIdRef) {
                listIdRef.current = wishlist?.id || null
            }
        }, [wishlist])

        const waitForRefs = async () => {
            const maxRetries = 10
            const interval = 100
            let retries = 0

            return new Promise((resolve, reject) => {
                const checkRefs = () => {
                    const listId = listIdRef.current
                    if (customerIdRef.current === customerId && listId) {
                        resolve()
                    } else if (retries < maxRetries) {
                        retries++
                        setTimeout(checkRefs, interval)
                    } else {
                        reject(new Error('Timeout waiting for customerId and listId'))
                    }
                }
                checkRefs()
            })
        }
        const location = useLocation()
        const getPageType = (pathname) => {
            if (pathname.endsWith('.html')) return 'pdp'
            if (pathname.startsWith('/search')) return 'search'
            if (pathname === '/' || pathname === '') return 'homepage'

            return 'other'
        }
        const pageType = getPageType(location.pathname)

        useEffect(() => {
            if (!dataLayerLoaded) {
                return
            }
            const isMobile = typeof window !== 'undefined' && window.innerWidth < 768
            const impressionBatchSize = 4
            const viewListBatchSize = isCompleteTheLook ? 2 : isMobile ? 4 : 6
            let taskIndex = 0
            for (let i = 0; i < (products?.length || 0); i += impressionBatchSize) {
                const startIndex = i
                scheduleIdleAnalytics(() => {
                    try {
                        const batch = products
                            ?.slice(startIndex, startIndex + impressionBatchSize)
                            .map((hit, index) => ({...hit, index: startIndex + index}))
                        tagManager.productImpression(batch, null, null, title)
                    } catch (e) {
                        console.log(e.message)
                    }
                }, 100 + taskIndex++ * 50)
            }
            for (let i = 0; i < (products?.length || 0); i += viewListBatchSize) {
                const startIndex = i
                scheduleIdleAnalytics(() => {
                    try {
                        const batch = products
                            ?.slice(startIndex, startIndex + viewListBatchSize)
                            .map((hit, index) => ({...hit, index: startIndex + index}))
                        tagManager.viewItemLists(batch, undefined, {
                            method: pageType,
                            listId: pageType,
                            listName: pageType
                        })
                    } catch (e) {
                        console.log(e.message)
                    }
                }, 100 + taskIndex++ * 50)
            }
        }, [products?.length, dataLayerLoaded])
        useEffect(() => {
            // Note: Please remove this once we move KS to PWA
            const resetStateOnBack = (event) => {
                if (event.persisted) {
                    // Page was restored from bfcache (back-forward cache)
                    setWishlistLoading(false)
                    setWishlistAnimation(false)
                    setShowAddtoCartSpinner(false)
                    setWishlistSpinner(false)
                }
            }
            window.addEventListener('pageshow', resetStateOnBack)
            return () => {
                window.removeEventListener('pageshow', resetStateOnBack)
            }
        }, [])
        /**************** Mutation Actions ****************/
        const createCustomerProductListItem = useShopperCustomersMutation(
            'createCustomerProductListItem'
        )
        const deleteCustomerProductListItem = useShopperCustomersMutation(
            'deleteCustomerProductListItem'
        )

        /**************** Action Handlers ****************/
        const addItemToWishlist = async (index, selectedProductId) => {
            if (!wishlist) {
                return
            }
            setWishlistLoading([...wishlistLoading, selectedProductId])
            monetate.setUsedWishlist()
            // TODO: This wishlist object is from an old API, we need to replace it with the new one.
            setWishlistSpinner(true)
            await waitForRefs()
            const listId = wishlist.id

            try {
                await createCustomerProductListItem.mutate(
                    {
                        parameters: {customerId, listId},
                        body: {
                            quantity: 1,
                            public: true,
                            priority: 1,
                            type: 'product',
                            productId: selectedProductId
                        }
                    },
                    {
                        onSuccess: () => {
                            setWishlistAnimation(index)
                            setWishlistSpinner(false)
                            setTimeout(() => {
                                setWishlistAnimation(index)
                            }, 1000)
                        },
                        onSettled: () => {
                            setWishlistSpinner(false)
                            setWishlistLoading(
                                wishlistLoading.filter((id) => id !== selectedProductId)
                            )
                        }
                    }
                )
            } catch (error) {
                setWishlistSpinner(false)
                logger.error('Error adding product to wishlist', {
                    namespace: 'ProductScroller.handleAddToWishlist',
                    additionalProperties: {error, productId: selectedProductId}
                })
            }
        }

        const removeItemFromWishlist = async (index, selectedProductId) => {
            setWishlistLoading([...wishlistLoading, selectedProductId])
            setWishlistSpinner(true)
            await waitForRefs()

            const listId = wishlist.id
            const itemId = wishlist.customerProductListItems.find(
                (i) => i.productId === selectedProductId
            ).id

            try {
                await deleteCustomerProductListItem.mutate(
                    {
                        body: {},
                        parameters: {customerId, listId, itemId}
                    },
                    {
                        onError: () => {
                            setWishlistSpinner(false)
                            setWishlistAnimation(false)
                        },
                        onSuccess: () => {
                            setWishlistSpinner(false)
                            setWishlistAnimation(false)
                        },
                        onSettled: () => {
                            setWishlistSpinner(false)
                            setWishlistLoading(
                                wishlistLoading.filter((id) => id !== selectedProductId)
                            )
                        }
                    }
                )
            } catch (error) {
                setWishlistSpinner(false)
                logger.error('Error adding product to wishlist', {
                    namespace: 'ProductScroller.removeItemFromWishlist',
                    additionalProperties: {error, productId: itemId}
                })
            }
        }

        // slider settings
        const sliderConfig = sliderVariant[sliderGroup]

        const lifestyleImageWidth = lifeStyle.enable ? {base: '253px', lg: '310px'} : 0

        useEffect(() => {
            if (!products?.length) return

            let observer
            let attempts = 0
            const maxAttempts = 10

            const interval = setInterval(() => {
                const element = document.getElementsByClassName('recommender-product-tile')[0]

                attempts++

                if (element) {
                    setTileHeight(element.offsetHeight)

                    observer = new ResizeObserver(() => {
                        setTileHeight(element.offsetHeight)
                    })

                    observer.observe(element)

                    clearInterval(interval)
                }

                if (attempts >= maxAttempts) {
                    clearInterval(interval)
                }
            }, 100)

            return () => {
                clearInterval(interval)

                if (observer) {
                    observer.disconnect()
                }
            }
        }, [products])

        return (
            <Box
                position="relative"
                className={type ? type : 'product-scroller'}
                sx={{
                    '& .wishlist-wrapper': {
                        display: countryISO === 'US' || countryISO === '' ? 'block' : 'none'
                    }
                }}
                data-testid="product-scroller"
                ref={ref}
                paddingY="40px"
            >
                {(wishlistSpinner || showAddtoCartSpinner) && (
                    <LoadingSpinner position="fixed" top="auto" />
                )}
                <Stack spacing="0" className={type ? type + '-stack' : 'product-scroller-stack'}>
                    {showTitle && !title && titleMinHeight && (
                        <Box className="product-scroller-title-slot" minHeight={titleMinHeight} />
                    )}

                    {isLoading && title && showTitle && (
                        <Skeleton
                            className="title-skeleton"
                            height={{base: '37px', lg: '48px'}}
                            width="200px"
                            marginLeft={{base: '20px', xs: '30px', lg: '40px'}}
                        />
                    )}

                    {title && !header && !isLoading && showTitle && (
                        <Text
                            as={titleTag}
                            className="list-title"
                            color={titleColor}
                            paddingLeft={{base: '20px', xs: '30px', lg: '40px'}}
                            fontFamily={titleFontFamily || 'caslon'}
                            fontSize={{base: '30px', lg: '40px'}}
                            letterSpacing={{base: '0.15px', lg: '0.2px'}}
                            lineHeight="normal"
                            fontWeight="400"
                            textAlign="left"
                            minHeight={titleMinHeight}
                        >
                            {title}
                        </Text>
                    )}
                    {/** NOTE: Don't show skeleton on 'isLoading'. **/}
                    {!products?.length ? (
                        <HStack
                            className="tiles-skeleton-wrapper"
                            marginLeft={{base: '20px', lg: '40px'}}
                            marginTop={'47px'}
                            gap={{base: '10px', lg: '8px'}}
                        >
                            {[0, 1].map((key) => {
                                return (
                                    <Box key={key} width={itemWidth} className="skeleton-box">
                                        <Stack
                                            data-testid="product-scroller-item-skeleton"
                                            className="product-scroller-item"
                                        >
                                            <Skeleton
                                                aspectRatio="auto 300 / 375"
                                                className="skeleton-tile-image"
                                            />
                                            <Stack
                                                spacing={2}
                                                className="product-scroller-item-stack"
                                            >
                                                <Skeleton
                                                    width="150px"
                                                    className="skeleton-tile-name"
                                                    height={5}
                                                />
                                                <Skeleton
                                                    width="75px"
                                                    className="skeleton-tile-price"
                                                    height={5}
                                                />
                                            </Stack>
                                        </Stack>
                                    </Box>
                                )
                            })}
                        </HStack>
                    ) : (
                        <Box
                            width={hideArrows ? 'auto' : '100%'}
                            maxWidth="100%"
                            className="swiper-parent"
                            sx={{
                                '.swiper': {
                                    paddingRight: '20px',
                                    paddingTop: '20px',
                                    marginTop: 0,

                                    '&:hover': {
                                        '.swiper-button-prev, .swiper-button-next': {
                                            opacity: 1
                                        }
                                    }
                                },
                                '.swiper-wrapper': {
                                    paddingLeft: hideArrows
                                        ? {base: '15px', sm: '30px', md: 0}
                                        : {base: '20px', xs: '30px', lg: '40px'},
                                    display: 'inline-flex'
                                },
                                '.swiper-slide': {
                                    width: itemWidth,
                                    marginRight: '8px',
                                    '&:last-child': {
                                        marginRight: hideArrows
                                            ? 0
                                            : {
                                                  base: '20px !important',
                                                  xs: '30px !important',
                                                  lg: '40px !important'
                                              }
                                    }
                                },
                                '.product-scroller-swiper-slide.lifestyle-image': {
                                    width: lifestyleImageWidth,
                                    height: tileHeight
                                },
                                '.swiper-button-prev, .swiper-button-next': {
                                    '--swiper-navigation-size': '45px',
                                    display: {base: 'none', md: 'flex'},
                                    top: '188px',
                                    borderRadius: '5px',
                                    boxShadow: '0px 1px 1px rgba(0, 0, 0, 0.20)',
                                    opacity: 0,
                                    transition: 'opacity 0.3s ease',

                                    '&.swiper-button-disabled': {
                                        display: 'none'
                                    }
                                },
                                '.swiper-button-prev': {
                                    left: '20px',
                                    right: 'initial',
                                    zIndex: '1 !important',
                                    transform: 'rotate(180deg)'
                                },
                                '.swiper-button-next': {
                                    right: '30px',
                                    left: 'initial',
                                    zIndex: '1 !important'
                                }
                            }}
                        >
                            <Swiper
                                navigation={!hideArrows}
                                slidesPerView={'auto'}
                                spaceBetween={spaceBetweenValue}
                                modules={[Navigation]}
                                loop={loop}
                                className="product-scroller-swiper"
                                grabCursor={grabCursor}
                                freeMode={freeMode}
                                mousewheel={mousewheel}
                                {...sliderConfig}
                            >
                                {renderedProducts?.map((product, idx) => {
                                    const selectableAttributeId = product?.variationValues?.[
                                        PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID_4
                                    ]
                                        ? PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID_4
                                        : product?.variationAttributes?.find((attr) =>
                                              [
                                                  PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID_1,
                                                  PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID_2,
                                                  PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID_3,
                                                  PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID_4
                                              ]?.includes(attr?.id)
                                          )?.id
                                    return (
                                        <React.Fragment
                                            key={product?.id || product?.productId || idx}
                                        >
                                            {lifeStyle.position === 'Start' &&
                                                lifeStyle.enable &&
                                                idx === 0 && (
                                                    <SwiperSlide
                                                        key={`lifestyle-image-${idx}`}
                                                        className="product-scroller-swiper-slide lifestyle-image"
                                                    >
                                                        <LifeStyleImage {...lifeStyle} />
                                                    </SwiperSlide>
                                                )}
                                            <SwiperSlide
                                                className="product-scroller-swiper-slide"
                                                onClick={onTileClick}
                                            >
                                                <ProductTile
                                                    dataTestid="product-scroller-item"
                                                    product={product}
                                                    tilesTitle={title}
                                                    hideVariations={hideVariations}
                                                    showImageSlider={showImageSlider}
                                                    isplp={false}
                                                    dataLayerLoaded={dataLayerLoaded}
                                                    index={idx}
                                                    quickAddLabel={quickAddLabel}
                                                    wishlistAnimation={wishlistAnimation}
                                                    wishlist={wishlist}
                                                    openQuickAddModal={openQuickAddModal}
                                                    selectedCurrency={currencyCode}
                                                    selectedCountry={countryISO}
                                                    onFavouriteToggle={(
                                                        toBeFavourite,
                                                        selectedProductId
                                                    ) => {
                                                        const action = toBeFavourite
                                                            ? addItemToWishlist
                                                            : removeItemFromWishlist
                                                        return action(idx, selectedProductId)
                                                    }}
                                                    setShowAddtoCartSpinner={(bool) => {
                                                        setShowAddtoCartSpinner(bool)
                                                    }}
                                                    showQuickAdd={showQuickAdd}
                                                    selectableAttributeId={selectableAttributeId}
                                                    {...(typeof productTileProps === 'function'
                                                        ? {...productTileProps(product)}
                                                        : {...productTileProps})}
                                                    dynamicImageProps={{
                                                        widths: [
                                                            '225w',
                                                            '450w',
                                                            '625w',
                                                            '800w',
                                                            '1000w'
                                                        ]
                                                    }}
                                                    enableFavourite={enableFavourite}
                                                    preventTileClick={preventTileClick}
                                                    showName={showName}
                                                    showPrice={showPrice}
                                                    analyticsProps={productAnalyticsProps[idx]}
                                                    showMetalLabel={showMetalLabel}
                                                />
                                            </SwiperSlide>
                                            {lifeStyle.position === 'End' &&
                                                lifeStyle.enable &&
                                                idx === products.length - 1 && (
                                                    <SwiperSlide
                                                        key={`lifestyle-image-${idx}`}
                                                        className="product-scroller-swiper-slide lifestyle-image"
                                                    >
                                                        <LifeStyleImage {...lifeStyle} />
                                                    </SwiperSlide>
                                                )}
                                        </React.Fragment>
                                    )
                                })}
                            </Swiper>
                        </Box>
                    )}
                </Stack>
            </Box>
        )
    }
)

ProductScroller.displayName = 'ProductScroller'

ProductScroller.propTypes = {
    header: PropTypes.any,
    title: PropTypes.any,
    titleMinHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object]),
    type: PropTypes.any,
    titleColor: PropTypes.string,
    titleFontFamily: PropTypes.string,
    titleTag: PropTypes.string,
    products: PropTypes.array,
    hideArrows: PropTypes.bool,
    isCompleteTheLook: PropTypes.bool,
    slickTrackMargin: PropTypes.any,
    quickAddLabel: PropTypes.bool,
    slidesToShow: PropTypes.any,
    showImageSlider: PropTypes.any,
    hideVariations: PropTypes.bool,
    dataLayerLoaded: PropTypes.bool,
    isLoading: PropTypes.bool,
    draggable: PropTypes.bool,
    showQuickAdd: PropTypes.bool,
    itemWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object]),
    productTileProps: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    showTitle: PropTypes.bool,
    enableFavourite: PropTypes.bool,
    onTileClick: PropTypes.func,
    preventTileClick: PropTypes.bool,
    spaceBetween: PropTypes.any,
    showName: PropTypes.bool,
    showPrice: PropTypes.bool,
    sliderGroup: PropTypes.string,
    loop: PropTypes.bool,
    grabCursor: PropTypes.bool,
    freeMode: PropTypes.bool,
    mousewheel: PropTypes.bool,
    openQuickAddModal: PropTypes.bool,
    analyticsProps: PropTypes.object,
    showMetalLabel: PropTypes.bool,
    lifeStyle: PropTypes.object,
    deferProductRendering: PropTypes.bool
}

export default ProductScroller
